import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import {
  Layers,
  BookOpen,
  Plus,
  CheckCircle2,
  AlertCircle,
  Shield,
  School,
  X,
  Search,
  Edit2,
  Trash2,
  GraduationCap,
  Tag
} from 'lucide-react';

interface Board {
  id: number;
  name: string;
  short_name: string;
  status: string;
}

interface ClassItem {
  id: number;
  name: string;
  display_order: number;
  status: string;
}

interface Subject {
  id: number;
  name: string;
  code: string;
  board_id?: number | null;
  short_code?: string | null;
  description?: string | null;
  status: string;
}

interface ClassSubjectMapping {
  id: number;
  board_id?: number | null;
  class_id: number;
  subject_id: number;
  stream?: string | null;
  medium?: string | null;
  subject_code?: string | null;
  is_compulsory: number;
  status: string;
  display_order: number;
  board_name?: string;
  board_code?: string;
  class_name: string;
  class_order?: number;
  subject_name: string;
}

const STREAMS = [
  { value: 'All', label: 'All Streams' },
  { value: 'Science', label: 'Science (Medical / Non-Med)' },
  { value: 'Commerce', label: 'Commerce' },
  { value: 'Arts', label: 'Arts / Humanities' },
  { value: 'Vocational', label: 'Vocational / Skill' },
  { value: 'General', label: 'General (Classes 6-10 / Common)' }
];

export const BoardClassSetupPage: React.FC = () => {
  const { fetchWithAuth } = useAuth();
  const { t } = useLanguage();

  const [boards, setBoards] = useState<Board[]>([]);
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [mappings, setMappings] = useState<ClassSubjectMapping[]>([]);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState<'matrix' | 'subjects' | 'structure'>('matrix');

  // Filters
  const [selectedBoardId, setSelectedBoardId] = useState<string>('All');
  const [selectedClassId, setSelectedClassId] = useState<string>('All');
  const [selectedStream, setSelectedStream] = useState<string>('All');
  const [selectedStatus, setSelectedStatus] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState<Subject | null>(null);
  const [newSubject, setNewSubject] = useState({
    name: '',
    code: '',
    board_id: '',
    description: '',
    status: 'active'
  });

  const [showMapModal, setShowMapModal] = useState(false);
  const [editingMapping, setEditingMapping] = useState<ClassSubjectMapping | null>(null);
  const [newMap, setNewMap] = useState({
    board_id: 1,
    class_id: 1,
    subject_id: 1,
    stream: '',
    is_compulsory: 1,
    subject_code: '',
    display_order: 1
  });

  const loadData = async () => {
    setLoading(true);
    try {
      const [bRes, cRes, sRes, mRes] = await Promise.all([
        fetchWithAuth('/api/boards'),
        fetchWithAuth('/api/classes'),
        fetchWithAuth('/api/subjects'),
        fetchWithAuth('/api/class-subjects')
      ]);

      const [bData, cData, sData, mData] = await Promise.all([
        bRes.json(),
        cRes.json(),
        sRes.json(),
        mRes.json()
      ]);

      if (bData.success) setBoards(bData.boards || []);
      if (cData.success) setClasses(cData.classes || []);
      if (sData.success) setSubjects(sData.subjects || []);
      if (mData.success) setMappings(mData.mappings || []);
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Error connecting to server. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Filtered Mappings
  const filteredMappings = useMemo(() => {
    return mappings.filter((m) => {
      if (selectedBoardId !== 'All' && m.board_id !== Number(selectedBoardId) && m.board_id !== null) {
        return false;
      }
      if (selectedClassId !== 'All' && m.class_id !== Number(selectedClassId)) {
        return false;
      }
      if (selectedStream !== 'All') {
        if (selectedStream === 'General') {
          if (m.stream && m.stream !== 'General') return false;
        } else if (m.stream !== selectedStream) {
          return false;
        }
      }
      if (selectedStatus !== 'All' && m.status !== selectedStatus) {
        return false;
      }
      if (searchQuery.trim()) {
        const subMatch = (m?.subject_name?.toLowerCase() ?? '').includes(q);
        const codeMatch = (m?.subject_code?.toLowerCase() ?? '').includes(q);
        const classMatch = (m?.class_name?.toLowerCase() ?? '').includes(q);
        const streamMatch = (m?.stream?.toLowerCase() ?? '').includes(q);
        if (!subMatch && !codeMatch && !classMatch && !streamMatch) return false;
      }
      return true;
    });
  }, [mappings, selectedBoardId, selectedClassId, selectedStream, selectedStatus, searchQuery]);

  // Filtered Subjects Master
  const filteredSubjects = useMemo(() => {
    return subjects.filter((s) => {
      if (selectedStatus !== 'All' && s.status !== selectedStatus) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (s?.name?.toLowerCase() ?? '').includes(q);
        const codeMatch = (s?.code?.toLowerCase() ?? '').includes(q);
        const descMatch = (s?.description?.toLowerCase() ?? '').includes(q);
        if (!nameMatch && !codeMatch && !descMatch) return false;
      }
      return true;
    });
  }, [subjects, selectedStatus, searchQuery]);

  // Handle Add or Edit Subject
  const handleSaveSubject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSubject.name.trim()) {
      setMessage({ type: 'error', text: 'Subject name is required.' });
      return;
    }

    try {
      const url = editingSubject ? `/api/subjects/${editingSubject.id}` : '/api/subjects';
      const method = editingSubject ? 'PUT' : 'POST';

      const payload = {
        name: newSubject.name.trim(),
        code: newSubject.code.trim().toUpperCase() || null,
        board_id: newSubject.board_id ? Number(newSubject.board_id) : null,
        description: newSubject.description.trim() || null,
        status: newSubject.status
      };

      const res = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setShowSubjectModal(false);
        setEditingSubject(null);
        setNewSubject({ name: '', code: '', board_id: '', description: '', status: 'active' });
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save subject.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    }
  };

  const handleOpenEditSubject = (sub: Subject) => {
    setEditingSubject(sub);
    setNewSubject({
      name: sub.name,
      code: sub.code || '',
      board_id: sub.board_id ? String(sub.board_id) : '',
      description: sub.description || '',
      status: sub.status
    });
    setShowSubjectModal(true);
  };

  const handleDeleteSubject = async (sub: Subject) => {
    if (!window.confirm(`Are you sure you want to deactivate or remove "${sub.name}"?`)) return;
    try {
      const res = await fetchWithAuth(`/api/subjects/${sub.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to remove subject.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    }
  };

  // Handle Save / Edit Mapping
  const handleSaveMapping = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = editingMapping ? `/api/class-subjects/${editingMapping.id}` : '/api/class-subjects';
      const method = editingMapping ? 'PUT' : 'POST';

      const payload = {
        board_id: newMap.board_id ? Number(newMap.board_id) : 1,
        class_id: Number(newMap.class_id),
        subject_id: Number(newMap.subject_id),
        stream: newMap.stream ? newMap.stream.trim() : null,
        is_compulsory: newMap.is_compulsory ? 1 : 0,
        subject_code: newMap.subject_code ? newMap.subject_code.trim().toUpperCase() : null,
        display_order: Number(newMap.display_order) || 1
      };

      const res = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setShowMapModal(false);
        setEditingMapping(null);
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to assign subject.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    }
  };

  const handleOpenEditMapping = (m: ClassSubjectMapping) => {
    setEditingMapping(m);
    setNewMap({
      board_id: m.board_id || 1,
      class_id: m.class_id,
      subject_id: m.subject_id,
      stream: m.stream || '',
      is_compulsory: m.is_compulsory,
      subject_code: m.subject_code || '',
      display_order: m.display_order || 1
    });
    setShowMapModal(true);
  };

  const handleDeleteMapping = async (m: ClassSubjectMapping) => {
    if (!window.confirm(`Are you sure you want to remove ${m.subject_name} from ${m.class_name} (${m.board_code || 'General'})?`)) return;
    try {
      const res = await fetchWithAuth(`/api/class-subjects/${m.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: data.message });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to remove mapping.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    }
  };

  const toggleMappingStatus = async (m: ClassSubjectMapping) => {
    try {
      const nextStatus = m.status === 'active' ? 'inactive' : 'active';
      const res = await fetchWithAuth(`/api/class-subjects/${m.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await res.json();
      if (data.success) {
        setMessage({ type: 'success', text: `Mapping status updated to ${nextStatus}.` });
        loadData();
      } else {
        setMessage({ type: 'error', text: data.message });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Server connection error.' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-[#0F2C59] text-white rounded-xl shadow-xs">
            <Layers className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-slate-900">
              {t('nav_board_class')} — Full Academic Hierarchy (Classes 6–12)
            </h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Multi-Board (HBSE & CBSE), Senior Secondary Streams (Science, Commerce, Arts, Vocational) & Relational Curriculum Mapping
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setEditingSubject(null);
              setNewSubject({ name: '', code: '', board_id: '', description: '', status: 'active' });
              setShowSubjectModal(true);
            }}
            className="bg-[#0F2C59] hover:bg-[#081B38] text-white text-xs font-bold py-2 px-3 rounded-lg shadow-xs flex items-center space-x-1.5 transition cursor-pointer"
          >
            <Plus className="w-4 h-4 text-amber-400" />
            <span>Add Subject</span>
          </button>
          <button
            onClick={() => {
              setEditingMapping(null);
              setNewMap({
                board_id: boards[0]?.id || 1,
                class_id: classes[0]?.id || 1,
                subject_id: subjects[0]?.id || 1,
                stream: '',
                is_compulsory: 1,
                subject_code: '',
                display_order: 1
              });
              setShowMapModal(true);
            }}
            className="bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold py-2 px-3 rounded-lg shadow-xs flex items-center space-x-1.5 transition cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Assign Subject to Class</span>
          </button>
        </div>
      </div>

      {/* Metric Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-blue-50 text-blue-700 rounded-lg">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Active Boards</div>
            <div className="text-xl font-black text-slate-900 mt-0.5">{boards.length} <span className="text-xs font-normal text-slate-500 font-mono">(HBSE, CBSE)</span></div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-emerald-50 text-emerald-700 rounded-lg">
            <School className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Active Classes</div>
            <div className="text-xl font-black text-slate-900 mt-0.5">{classes.length} <span className="text-xs font-normal text-slate-500 font-mono">(Class 6 to 12)</span></div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-indigo-50 text-indigo-700 rounded-lg">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Subject Master</div>
            <div className="text-xl font-black text-slate-900 mt-0.5">{subjects.length} <span className="text-xs font-normal text-slate-500 font-mono">Catalog</span></div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs flex items-center space-x-3">
          <div className="p-2.5 bg-amber-50 text-amber-700 rounded-lg">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Curriculum Mappings</div>
            <div className="text-xl font-black text-slate-900 mt-0.5">{mappings.length} <span className="text-xs font-normal text-slate-500 font-mono">Allocations</span></div>
          </div>
        </div>
      </div>

      {/* Message Banner */}
      {message && (
        <div
          className={`p-4 rounded-xl text-xs flex items-center justify-between border ${
            message.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border-emerald-300'
              : 'bg-rose-50 text-rose-800 border-rose-300'
          }`}
        >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
            )}
            <span className="font-semibold">{message.text}</span>
          </div>
          <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
        <div className="flex border-b border-slate-200 bg-slate-50/70 p-1.5 space-x-1">
          <button
            onClick={() => setActiveTab('matrix')}
            className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 py-2.5 px-5 rounded-lg text-xs font-bold transition cursor-pointer ${
              activeTab === 'matrix'
                ? 'bg-[#0F2C59] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            <BookOpen className="w-4 h-4 text-amber-400" />
            <span>Class-wise Curriculum Matrix</span>
            <span className="ml-1.5 px-2 py-0.5 bg-white/20 rounded-full text-[10px] font-mono">
              {filteredMappings.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('subjects')}
            className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 py-2.5 px-5 rounded-lg text-xs font-bold transition cursor-pointer ${
              activeTab === 'subjects'
                ? 'bg-[#0F2C59] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            <Tag className="w-4 h-4 text-amber-400" />
            <span>Subject Master Catalog</span>
            <span className="ml-1.5 px-2 py-0.5 bg-white/20 rounded-full text-[10px] font-mono">
              {filteredSubjects.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('structure')}
            className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 py-2.5 px-5 rounded-lg text-xs font-bold transition cursor-pointer ${
              activeTab === 'structure'
                ? 'bg-[#0F2C59] text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
            }`}
          >
            <School className="w-4 h-4 text-amber-400" />
            <span>Board & Class Structure</span>
          </button>
        </div>

        {/* Global Filter Bar */}
        <div className="p-4 bg-white border-b border-slate-100 flex flex-wrap items-center gap-3 text-xs">
          {/* Search Box */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by subject, code, class..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-[#0F2C59] focus:outline-hidden"
            />
          </div>

          {/* Board Filter */}
          <div className="flex items-center space-x-1.5">
            <span className="font-bold text-slate-600">Board:</span>
            <select
              value={selectedBoardId}
              onChange={(e) => setSelectedBoardId(e.target.value)}
              className="py-2 px-3 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:ring-2 focus:ring-[#0F2C59]"
            >
              <option value="All">All Boards</option>
              {boards.map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.short_name} ({b.name})
                </option>
              ))}
            </select>
          </div>

          {/* Class Filter */}
          <div className="flex items-center space-x-1.5">
            <span className="font-bold text-slate-600">Class:</span>
            <select
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(e.target.value)}
              className="py-2 px-3 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:ring-2 focus:ring-[#0F2C59]"
            >
              <option value="All">All Classes (6–12)</option>
              {classes.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Stream Filter */}
          <div className="flex items-center space-x-1.5">
            <span className="font-bold text-slate-600">Stream:</span>
            <select
              value={selectedStream}
              onChange={(e) => setSelectedStream(e.target.value)}
              className="py-2 px-3 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:ring-2 focus:ring-[#0F2C59]"
            >
              {STREAMS.map((st) => (
                <option key={st.value} value={st.value}>
                  {st.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center space-x-1.5">
            <span className="font-bold text-slate-600">Status:</span>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="py-2 px-3 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold focus:ring-2 focus:ring-[#0F2C59]"
            >
              <option value="All">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          <button
            onClick={() => {
              setSelectedBoardId('All');
              setSelectedClassId('All');
              setSelectedStream('All');
              setSelectedStatus('All');
              setSearchQuery('');
            }}
            className="text-slate-500 hover:text-slate-800 text-xs font-semibold underline px-2 py-1 cursor-pointer"
          >
            Reset
          </button>
        </div>

        {/* Tab 1: Curriculum Matrix */}
        {activeTab === 'matrix' && (
          <div className="p-5 space-y-6">
            {classes
              .filter((c) => selectedClassId === 'All' || String(c.id) === selectedClassId)
              .map((c) => {
                const classMappings = filteredMappings.filter((m) => m.class_id === c.id);
                if (classMappings.length === 0 && selectedClassId !== 'All') {
                  return (
                    <div key={c.id} className="p-8 text-center bg-slate-50 rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-500">No subject mappings match your current filter for {c.name}.</p>
                    </div>
                  );
                }
                if (classMappings.length === 0) return null;

                return (
                  <div key={c.id} className="bg-slate-50/80 rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between pb-2 border-b border-slate-200 gap-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-black text-slate-900 bg-white px-3 py-1 rounded-lg border border-slate-200 shadow-2xs">
                          {c.name}
                        </span>
                        <span className="text-xs text-slate-500">
                          {Number(c.name.replace(/\D/g, '')) >= 11 ? 'Senior Secondary (Stream Wise)' : 'Secondary / Middle Curriculum'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-[11px] bg-blue-100 text-blue-900 font-bold px-2.5 py-0.5 rounded-full font-mono">
                          {classMappings.length} Subjects Allocated
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                      {classMappings.map((m) => {
                        const isCompulsory = m.is_compulsory === 1;
                        const boardCode = m.board_code || (m.board_id === 2 ? 'CBSE' : 'HBSE');
                        return (
                          <div
                            key={m.id}
                            className={`p-3 bg-white rounded-xl border transition shadow-2xs hover:shadow-xs flex flex-col justify-between space-y-2.5 ${
                              m.status === 'inactive' ? 'opacity-60 border-slate-300 bg-slate-100/60' : 'border-slate-200'
                            }`}
                          >
                            <div>
                              <div className="flex items-center justify-between gap-1.5 mb-1.5">
                                <span
                                  className={`text-[10px] font-bold px-2 py-0.5 rounded font-mono ${
                                    boardCode === 'CBSE'
                                      ? 'bg-blue-100 text-blue-800'
                                      : 'bg-amber-100 text-amber-900'
                                  }`}
                                >
                                  {boardCode}
                                </span>

                                {m.stream ? (
                                  <span className="text-[10px] font-semibold bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                                    {m.stream}
                                  </span>
                                ) : (
                                  <span className="text-[10px] font-normal text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                                    General
                                  </span>
                                )}
                              </div>

                              <div className="font-bold text-slate-900 text-xs line-clamp-1">
                                {m.subject_name}
                              </div>

                              <div className="flex items-center justify-between mt-1 text-[11px] text-slate-500">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px]">
                                  {m.subject_code || 'CODE-NA'}
                                </span>
                                <span
                                  className={`font-semibold text-[10px] ${
                                    isCompulsory ? 'text-indigo-700' : 'text-slate-500'
                                  }`}
                                >
                                  {isCompulsory ? '★ Compulsory' : 'Optional / Elective'}
                                </span>
                              </div>
                            </div>

                            <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                              <button
                                onClick={() => toggleMappingStatus(m)}
                                className={`font-bold px-1.5 py-0.5 rounded text-[10px] cursor-pointer ${
                                  m.status === 'active'
                                    ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                                    : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                                }`}
                              >
                                {m.status === 'active' ? '● Active' : '○ Inactive'}
                              </button>

                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleOpenEditMapping(m)}
                                  className="p-1 text-slate-400 hover:text-[#0F2C59] hover:bg-slate-100 rounded cursor-pointer"
                                  title="Edit Mapping"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteMapping(m)}
                                  className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded cursor-pointer"
                                  title="Remove Mapping"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Tab 2: Subject Master Catalog */}
        {activeTab === 'subjects' && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  District Standardized Subjects Master
                </h3>
                <p className="text-[11px] text-slate-500">
                  Reusable subject definitions across all HBSE & CBSE classes and examination assessments
                </p>
              </div>
              <button
                onClick={() => {
                  setEditingSubject(null);
                  setNewSubject({ name: '', code: '', board_id: '', description: '', status: 'active' });
                  setShowSubjectModal(true);
                }}
                className="bg-[#0F2C59] text-white text-xs font-bold py-1.5 px-3 rounded-lg shadow-xs flex items-center space-x-1.5 cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5 text-amber-400" />
                <span>Add New Subject</span>
              </button>
            </div>

            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-100/80 text-slate-700 font-bold border-b border-slate-200">
                  <tr>
                    <th className="py-3 px-4">Subject Code</th>
                    <th className="py-3 px-4">Subject Name</th>
                    <th className="py-3 px-4">Description</th>
                    <th className="py-3 px-4">Class Allocations</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {filteredSubjects.map((s) => {
                    const usageCount = mappings.filter((m) => m.subject_id === s.id).length;
                    return (
                      <tr key={s.id} className="hover:bg-slate-50/70 transition">
                        <td className="py-3 px-4 font-mono font-bold text-slate-900">
                          <span className="bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                            {s.code || 'CODE-NA'}
                          </span>
                        </td>
                        <td className="py-3 px-4 font-bold text-slate-900">{s.name}</td>
                        <td className="py-3 px-4 text-slate-500 max-w-xs truncate">
                          {s.description || '—'}
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px]">
                          <span className="bg-blue-50 text-blue-800 font-bold px-2 py-0.5 rounded">
                            {usageCount} Classes
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`font-bold text-[10px] px-2 py-0.5 rounded-full ${
                              s.status === 'active'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {s.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end space-x-1.5">
                            <button
                              onClick={() => handleOpenEditSubject(s)}
                              className="p-1.5 text-slate-500 hover:text-[#0F2C59] hover:bg-slate-100 rounded-md transition cursor-pointer"
                              title="Edit Subject"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteSubject(s)}
                              className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-md transition cursor-pointer"
                              title="Deactivate / Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 3: Boards & Classes Structure Overview */}
        {activeTab === 'structure' && (
          <div className="p-5 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Boards List */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center space-x-2 pb-2 border-b border-slate-100">
                  <Shield className="w-4 h-4 text-[#0F2C59]" />
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    {t('boardsList')} (Separate & Isolated)
                  </h2>
                </div>
                <div className="space-y-2">
                  {boards.map((b) => (
                    <div
                      key={b.id}
                      className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 flex justify-between items-center"
                    >
                      <div>
                        <div className="font-bold text-slate-900 text-sm flex items-center space-x-2">
                          <span>{b.name}</span>
                          <span className="bg-amber-100 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded font-mono">
                            {b.short_name}
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          Isolated analytics & marksheets pipeline
                        </div>
                      </div>
                      <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        ● ACTIVE
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Classes List */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-5 space-y-3">
                <div className="flex items-center space-x-2 pb-2 border-b border-slate-100">
                  <School className="w-4 h-4 text-[#0F2C59]" />
                  <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    {t('classesList')} (Classes 6 to 12)
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {classes.map((c) => {
                    const isSenior = Number(c.name.replace(/\D/g, '')) >= 11;
                    return (
                      <div
                        key={c.id}
                        className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex justify-between items-center"
                      >
                        <div>
                          <div className="font-bold text-slate-900 text-xs">{c.name}</div>
                          <div className="text-[10px] text-slate-500">
                            {isSenior ? 'Streams (Sci/Com/Arts)' : 'Middle / Secondary'}
                          </div>
                        </div>
                        <span className="bg-emerald-100 text-emerald-800 text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                          ACTIVE
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Subject Modal */}
      {showSubjectModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-300 overflow-hidden">
            <div className="bg-[#0F2C59] text-white px-5 py-4 flex justify-between items-center border-b-2 border-amber-500">
              <h3 className="text-sm font-bold">
                {editingSubject ? 'Edit Subject Details' : 'Add New Standard Subject'}
              </h3>
              <button
                onClick={() => {
                  setShowSubjectModal(false);
                  setEditingSubject(null);
                }}
                className="text-slate-300 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveSubject} className="p-6 space-y-4 text-xs">
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Subject Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newSubject.name}
                  onChange={(e) => setNewSubject({ ...newSubject, name: e.target.value })}
                  placeholder="e.g. Accountancy, History, Computer Science"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Subject Code
                </label>
                <input
                  type="text"
                  value={newSubject.code}
                  onChange={(e) => setNewSubject({ ...newSubject, code: e.target.value })}
                  placeholder="e.g. ACC-113, HIS-116, CSC-111"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50 font-mono"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Description / Syllabus Scope
                </label>
                <textarea
                  rows={2}
                  value={newSubject.description}
                  onChange={(e) => setNewSubject({ ...newSubject, description: e.target.value })}
                  placeholder="e.g. Senior Secondary Commerce Core Curriculum"
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#0F2C59] bg-slate-50"
                />
              </div>

              {editingSubject && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Status
                  </label>
                  <select
                    value={newSubject.status}
                    onChange={(e) => setNewSubject({ ...newSubject, status: e.target.value })}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubjectModal(false);
                    setEditingSubject(null);
                  }}
                  className="bg-slate-200 text-slate-700 text-xs font-bold py-2 px-4 rounded-lg cursor-pointer"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="bg-[#0F2C59] text-white text-xs font-bold py-2 px-5 rounded-lg shadow-sm cursor-pointer"
                >
                  {editingSubject ? 'Update Subject' : 'Save Subject'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Map Subject to Class Modal */}
      {showMapModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full border border-slate-300 overflow-hidden">
            <div className="bg-[#0F2C59] text-white px-5 py-4 flex justify-between items-center border-b-2 border-amber-500">
              <h3 className="text-sm font-bold">
                {editingMapping ? 'Edit Curriculum Mapping' : 'Assign Subject to Class Curriculum'}
              </h3>
              <button
                onClick={() => {
                  setShowMapModal(false);
                  setEditingMapping(null);
                }}
                className="text-slate-300 hover:text-white cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveMapping} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Select Board <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={newMap.board_id}
                    onChange={(e) => setNewMap({ ...newMap, board_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold"
                  >
                    {boards.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.short_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Select Class <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={newMap.class_id}
                    onChange={(e) => setNewMap({ ...newMap, class_id: Number(e.target.value) })}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold"
                  >
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Stream (Classes 11 & 12)
                </label>
                <select
                  value={newMap.stream}
                  onChange={(e) => setNewMap({ ...newMap, stream: e.target.value })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold"
                >
                  <option value="">General / None (Classes 6-10 / All Streams)</option>
                  <option value="Science">Science (Medical / Non-Medical)</option>
                  <option value="Commerce">Commerce</option>
                  <option value="Arts">Arts / Humanities</option>
                  <option value="Vocational">Vocational / Skill</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                  Select Subject <span className="text-rose-500">*</span>
                </label>
                <select
                  value={newMap.subject_id}
                  onChange={(e) => setNewMap({ ...newMap, subject_id: Number(e.target.value) })}
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-semibold"
                >
                  {subjects.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.code || 'NO-CODE'})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Custom Subject Code
                  </label>
                  <input
                    type="text"
                    value={newMap.subject_code}
                    onChange={(e) => setNewMap({ ...newMap, subject_code: e.target.value })}
                    placeholder="Optional Override"
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={newMap.display_order}
                    onChange={(e) => setNewMap({ ...newMap, display_order: Number(e.target.value) || 1 })}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg bg-slate-50 font-mono"
                  />
                </div>
              </div>

              <div className="pt-1">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newMap.is_compulsory === 1}
                    onChange={(e) => setNewMap({ ...newMap, is_compulsory: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 text-[#0F2C59] rounded border-slate-300 focus:ring-[#0F2C59]"
                  />
                  <span className="font-semibold text-slate-800">
                    Compulsory Subject for this Board & Class
                  </span>
                </label>
              </div>

              <div className="pt-2 flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowMapModal(false);
                    setEditingMapping(null);
                  }}
                  className="bg-slate-200 text-slate-700 text-xs font-bold py-2 px-4 rounded-lg cursor-pointer"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="bg-[#0F2C59] text-white text-xs font-bold py-2 px-5 rounded-lg shadow-sm cursor-pointer"
                >
                  {editingMapping ? 'Update Mapping' : 'Assign to Class'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
